/**
 * ProjectMembers Component
 * Manages project team members with invitations and user assignment
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { UserPlus, X, Copy, Check, Crown, ArrowRightLeft } from 'lucide-react';
import { API, getApiUrl } from '../../utils/api';
import { LANGUAGES } from '../../constants/languages';
import AddMemberModal from '../AddMemberModal';

const ProjectMembers = ({ project, currentUser, onProjectUpdated }) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [inviteCode, setInviteCode] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    if (project) {
      loadProjectMembers();
      generateInviteCode();
    }
  }, [project]);

  const loadProjectMembers = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/projects/${project.id}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Error loading project members:', error);
    }
  };

  const handleMemberAdded = (newMember) => {
    // Refresh the members list
    loadProjectMembers();
  };

  const removeUserFromProject = async (userId) => {
    if (!window.confirm(t('team.confirmRemove', 'Mitglied aus dem Projekt entfernen?'))) return;
    
    try {
      await axios.delete(`${getApiUrl()}/projects/${project.id}/members/${userId}?remover_id=${currentUser.id}`);
      loadProjectMembers();
    } catch (error) {
      console.error('Error removing user from project:', error);
      alert(t('team.removeError', 'Fehler beim Entfernen des Benutzers') + ': ' + (error.response?.data?.detail || error.message));
    }
  };

  const transferLeadership = async (newLeaderId, newLeaderName) => {
    if (!window.confirm(t('team.confirmTransfer', 'Möchten Sie die Projektleitung wirklich an "{{name}}" übergeben?\n\nSie verlieren damit die Berechtigung, Mitglieder zu entfernen.').replace('{{name}}', newLeaderName))) {
      return;
    }
    
    setTransferring(true);
    try {
      await axios.put(
        `${getApiUrl()}/projects/${project.id}/transfer-leadership?current_leader_id=${currentUser.id}&new_leader_id=${newLeaderId}`
      );
      alert(t('team.transferSuccess', 'Projektleitung wurde erfolgreich an "{{name}}" übertragen.').replace('{{name}}', newLeaderName));
      loadProjectMembers();
      // Notify parent to refresh project data
      if (onProjectUpdated) {
        onProjectUpdated();
      }
    } catch (error) {
      console.error('Error transferring leadership:', error);
      alert(t('team.transferError', 'Fehler beim Übertragen der Leitung') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setTransferring(false);
    }
  };

  const generateInviteCode = async () => {
    try {
      const response = await axios.post(`${getApiUrl()}/projects/${project.id}/invite`);
      setInviteCode(response.data.invite_code);
    } catch (error) {
      console.error('Error generating invite code:', error);
      const code = `BL-${project.id.slice(-8).toUpperCase()}`;
      setInviteCode(code);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const sendInviteViaSMS = (code, projectName) => {
    const appUrl = window.location.origin;
    const inviteUrl = `${appUrl}?invite=${code}`;
    
    const message = encodeURIComponent(
      `🏗️ ${t('invite.smsTitle', 'Invitation to Babelmix!')}\n\n` +
      `${t('invite.smsInvited', 'You have been invited to the project')} "${projectName}".\n\n` +
      `${t('invite.smsRegister', 'Register here')}:\n${inviteUrl}\n\n` +
      `${t('invite.smsOrCode', 'Or enter the code')} "${code}" ${t('invite.smsDuringRegistration', 'during registration')}.\n\n` +
      `${t('invite.smsTeam', 'Your Babelmix Team')}`
    );
    
    const smsUrl = `sms:?body=${message}`;
    window.location.href = smsUrl;
  };
  
  const sendInviteViaEmail = (code, projectName) => {
    const appUrl = window.location.origin;
    const inviteUrl = `${appUrl}?invite=${code}`;
    
    const subject = encodeURIComponent(t('invite.emailSubject', 'Invitation to Babelmix - Project: {{project}}').replace('{{project}}', projectName));
    const body = encodeURIComponent(
      `${t('invite.emailGreeting', 'Hello!')}\n\n` +
      `${t('invite.emailInvited', 'You have been invited to collaborate on the Babelmix project')} "${projectName}".\n\n` +
      `${t('invite.emailClickLink', 'To join the project, please click the following link')}:\n` +
      `${inviteUrl}\n\n` +
      `${t('invite.emailAlternative', 'Alternatively, you can register at {{url}} and enter the invitation code')} "${code}".\n\n`.replace('{{url}}', appUrl) +
      `${t('invite.emailWhatIs', 'What is Babelmix?')}\n` +
      `${t('invite.emailDescription', 'Babelmix is a communication platform for teams that enables real-time communication, file sharing, and project coordination.')}\n\n` +
      `${t('invite.emailContact', 'If you have questions, please contact the project leader.')}\n\n` +
      `${t('invite.emailRegards', 'Best regards')}\n` +
      `${t('invite.emailTeam', 'Your Babelmix Team')}`
    );
    
    const mailUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailUrl;
  };

  const isLeader = project.creator_id === currentUser.id;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="font-semibold text-gray-800 mb-4">{t('projects.members', 'Project Members')} ({members.length})</h3>
        <div className="space-y-3">
          {members.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('team.noMembersYet', 'No members yet')}</p>
          ) : (
            members.map(member => {
              const isMemberLeader = member.id === project.creator_id;
              const isCurrentUser = member.id === currentUser.id;
              
              return (
                <div key={member.id} className={`flex items-center p-3 rounded-lg ${isMemberLeader ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                  <Avatar className="w-10 h-10 mr-3">
                    <AvatarFallback className={`${isMemberLeader ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {member.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-800 truncate">{member.name}</p>
                      {isMemberLeader && (
                        <Badge className="bg-amber-500 text-white text-xs flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          {t('team.leader', 'Leiter')}
                        </Badge>
                      )}
                      {isCurrentUser && !isMemberLeader && (
                        <Badge variant="secondary" className="text-xs">{t('team.you', 'Du')}</Badge>
                      )}
                      {isCurrentUser && isMemberLeader && (
                        <Badge variant="secondary" className="text-xs">{t('team.you', 'Du')}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {LANGUAGES.find(l => l.code === member.language)?.name}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-2">
                    {/* Transfer leadership button - only visible to current leader for other members */}
                    {isLeader && !isCurrentUser && !isMemberLeader && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => transferLeadership(member.id, member.name)}
                        disabled={transferring}
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        title={t('team.transferLeadership', 'Projektleitung übergeben')}
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {/* Remove member button - only visible to leader for non-leaders */}
                    {isLeader && !isCurrentUser && !isMemberLeader && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeUserFromProject(member.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title={t('team.removeMember', 'Aus Projekt entfernen')}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <h3 className="font-semibold text-gray-800">{t('team.manageMembers', 'Teilnehmer verwalten')}</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => setShowAddMemberModal(true)}
              className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
              data-testid="add-member-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              {t('team.add', 'Hinzufügen')}
            </Button>
            <Button
              size="sm"
              onClick={() => setShowInvite(!showInvite)}
              className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none"
              data-testid="show-invite-code-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              {t('team.invite', 'Einladen')}
            </Button>
          </div>
        </div>

        {showInvite && (
          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('team.inviteCode', 'Einladungscode')}
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={inviteCode}
                    readOnly
                    className="flex-1 bg-white"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={copyInviteCode}
                      variant="outline"
                      className="flex items-center gap-1 flex-1 sm:flex-none justify-center"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? t('common.copied', 'Kopiert!') : t('common.copy', 'Kopieren')}
                    </Button>
                    
                    <Button
                      size="sm"
                      onClick={() => sendInviteViaSMS(inviteCode, project.name)}
                      variant="outline"
                      className="flex items-center gap-1 bg-blue-50 hover:bg-blue-100 flex-1 sm:flex-none justify-center"
                      title={t('team.openSmsApp', 'SMS-App mit Einladung öffnen')}
                    >
                      💬 SMS
                    </Button>
                    
                    <Button
                      size="sm"
                      onClick={() => sendInviteViaEmail(inviteCode, project.name)}
                      variant="outline"
                      className="flex items-center gap-1 bg-green-50 hover:bg-green-100 flex-1 sm:flex-none justify-center"
                      title={t('team.openEmailApp', 'E-Mail-App mit Einladung öffnen')}
                    >
                      📧 {t('team.email', 'E-Mail')}
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {t('team.inviteDescription', 'Teilen Sie diesen Code mit neuen Teammitgliedern. Sie können sich dann mit diesem Code registrieren und werden automatisch dem Projekt hinzugefügt.')}
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        projectId={project?.id}
        currentUser={currentUser}
        existingMembers={members}
        onMemberAdded={handleMemberAdded}
      />
    </div>
  );
};

export default ProjectMembers;
